import { useRef, useState } from "react"
import {
    Badge, Box, Button, ButtonGroup, Divider, Flex, Grid, GridItem,
    Input, NumberInput, NumberInputField, NumberInputStepper,
    NumberIncrementStepper, NumberDecrementStepper,
    Select, Spinner, Text, Textarea,
    useColorModeValue, useDisclosure, useToast
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { Container, Heading, Icon, IconButton, Tag } from "components"
import { API } from "config"
import { useAllDistributors } from "hooks"
import { Authenticate } from "screens"

// ─── Hooks ────────────────────────────────────────────────────────────────────

const useProductSearch = (term) => useQuery({
    queryKey: ['product-search-adj', term],
    queryFn: async () => {
        if (!term || term.length < 2) return []
        const { data } = await API.get(`/products?term=${encodeURIComponent(term)}`)
        return Array.isArray(data) ? data : (data?.data ?? [])
    },
    enabled: term?.length >= 2,
    staleTime: 30000,
})

const useCurrentStock = (distributorId, productId) => useQuery({
    queryKey: ['adj-stock', distributorId, productId],
    queryFn: async () => {
        const { data } = await API.get(`/stock/inventory?distributor_id=${distributorId}&product_id=${productId}&per_page=1`)
        return data?.data?.[0] ?? null
    },
    enabled: !!distributorId && !!productId,
    staleTime: 10000,
})

const useDistributorLots = (distributorId, productName) => useQuery({
    queryKey: ['distributor-lots-adj', distributorId, productName],
    queryFn: async () => {
        if (!distributorId || !productName) return { data: [] }
        const params = new URLSearchParams({ per_page: 50, term: productName })
        const { data } = await API.get(`/distributors/${distributorId}/stock/lots?${params}`)
        return data
    },
    enabled: !!distributorId && !!productName,
    staleTime: 15000,
})

// ─── Componente principal ─────────────────────────────────────────────────────

export default function Adjustments() {
    const [distributorId, setDistributorId]     = useState('')
    const [productId, setProductId]             = useState('')
    const [productName, setProductName]         = useState('')
    const [searchTerm, setSearchTerm]           = useState('')
    const [showDropdown, setShowDropdown]       = useState(false)
    const [selectedLotId, setSelectedLotId]     = useState('')
    const [selectedLotNumber, setSelectedLotNumber] = useState('')
    const [selectedLotQty, setSelectedLotQty]   = useState(0)
    const [operation, setOperation]             = useState('add')
    const [qty, setQty]                         = useState(1)
    const [reason, setReason]                   = useState('')
    const [loading, setLoading]                 = useState(false)
    const [errors, setErrors]                   = useState({})
    const searchRef = useRef(null)

    const toast = useToast()
    const auth  = useDisclosure()

    const distributors    = useAllDistributors()
    const warehouses      = distributors.data?.filter(d => d.is_warehouse) ?? []
    const productSearch   = useProductSearch(searchTerm)
    const currentStock    = useCurrentStock(distributorId, productId)
    const distributorLots = useDistributorLots(distributorId, productName)

    // ── Colors ───────────────────────────────────────────────────────────────
    const cardBg         = useColorModeValue('white',      'gray.800')
    const cardBorder     = useColorModeValue('gray.200',   'gray.700')
    const labelColor     = useColorModeValue('gray.500',   'gray.400')
    const dropdownBg     = useColorModeValue('white',      'gray.800')
    const dropdownHover  = useColorModeValue('gray.50',    'gray.700')
    const stockRowBg     = useColorModeValue('gray.50',    'gray.750')
    const stockRowBorder = useColorModeValue('gray.100',   'gray.600')
    const addPanelBg     = useColorModeValue('green.50',   'gray.750')
    const addPanelBorder = useColorModeValue('green.100',  'green.800')
    const subPanelBg     = useColorModeValue('red.50',     'gray.750')
    const subPanelBorder = useColorModeValue('red.100',    'red.800')
    const formSectionBg  = useColorModeValue('gray.50',    'gray.750')
    const lotCardBorder  = useColorModeValue('gray.200',   'gray.600')
    const lotCardHover   = useColorModeValue('teal.50',    'gray.700')
    const lotPanelBg     = useColorModeValue('gray.50',    'gray.750')
    const lotPanelSelBg  = useColorModeValue('teal.50',    'teal.900')

    const isAdd      = operation === 'add'
    const opColor    = isAdd ? 'green' : 'red'
    const opPanelBg  = isAdd ? addPanelBg  : subPanelBg
    const opPanelBord = isAdd ? addPanelBorder : subPanelBorder

    // ── Handlers ─────────────────────────────────────────────────────────────

    const clearLot = () => {
        setSelectedLotId('')
        setSelectedLotNumber('')
        setSelectedLotQty(0)
    }

    const handleSelectProduct = (product) => {
        setProductId(product.id)
        setProductName(product.name)
        setSearchTerm(product.name)
        setShowDropdown(false)
        setErrors(v => ({ ...v, product: null }))
        clearLot()
    }

    const handleSelectLot = (lot) => {
        if (selectedLotId === lot.id) {
            clearLot()
        } else {
            setSelectedLotId(lot.id)
            setSelectedLotNumber(lot.lot_number)
            setSelectedLotQty(lot.quantity)
            setErrors(v => ({ ...v, qty: null }))
        }
    }

    const validate = () => {
        const e = {}
        if (!distributorId)             e.distributor = 'Selecione um núcleo'
        if (!productId)                 e.product     = 'Selecione um produto'
        if (!qty || qty < 1)            e.qty         = 'Mínimo 1'
        if (reason.trim().length < 10)  e.reason      = 'Mínimo 10 caracteres'
        if (operation === 'subtract') {
            if (currentStock.data && qty > currentStock.data.available) {
                e.qty = `Máximo disponível no núcleo: ${currentStock.data.available} un.`
            }
            if (selectedLotId && qty > selectedLotQty) {
                e.qty = `Máximo no lote ${selectedLotNumber}: ${selectedLotQty} un.`
            }
        }
        setErrors(e)
        return Object.keys(e).length === 0
    }

    const handleSubmit = async () => {
        setLoading(true)
        try {
            const res = await API.post('/stock/adjustments', {
                product_id:      productId,
                distributor_id:  distributorId,
                operation,
                quantity:        Number(qty),
                reason:          reason.trim(),
                ...(selectedLotId && { product_lot_id: selectedLotId }),
            })
            toast({ title: res.data.message, status: 'success', duration: 5000, isClosable: true })
            setProductId(''); setProductName(''); setSearchTerm('')
            setQty(1); setReason(''); setErrors({})
            clearLot()
        } catch (err) {
            toast({ title: err?.response?.data?.message ?? 'Erro ao realizar ajuste', status: 'error', isClosable: true })
        } finally {
            setLoading(false)
        }
    }

    const stock = currentStock.data
    const lots  = distributorLots.data?.data ?? []

    return (
        <Container maxW="90%" mt={10}>
            <Heading title="Ajuste Manual de Estoque" />
            <Text color={labelColor} fontSize="sm" mt={1} mb={8}>
                Corrija divergências de saldo diretamente no estoque físico.
                Todos os ajustes são registrados no ledger de movimentações com autoria e motivo obrigatório.
            </Text>

            <Grid templateColumns={{ base: '1fr', xl: '1fr 380px' }} gap={6} alignItems="start">

                {/* ── Formulário ─────────────────────────────────────── */}
                <GridItem>
                    <Box bg={cardBg} border="1px solid" borderColor={cardBorder} borderRadius="2xl" overflow="hidden">

                        {/* Header */}
                        <Flex align="center" gap={3} px={7} py={5} borderBottom="1px solid" borderColor={cardBorder}>
                            <Flex align="center" justify="center" w={10} h={10}
                                bg={`${opColor}.100`} borderRadius="xl" flexShrink={0}
                                _dark={{ bg: `${opColor}.900` }}>
                                <Icon name={isAdd ? 'addcircle' : 'losses'} boxSize={5} color={`${opColor}.600`}
                                    _dark={{ color: `${opColor}.300` }} />
                            </Flex>
                            <Box>
                                <Text fontWeight="semibold" fontSize="md" lineHeight={1.2}>Configurar ajuste</Text>
                                <Text fontSize="xs" color={labelColor} mt={0.5}>Selecione núcleo, produto e operação</Text>
                            </Box>
                        </Flex>

                        <Box p={7} display="flex" flexDirection="column" gap={6}>

                            {/* Núcleo + Produto lado a lado */}
                            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={5}>
                                <Box>
                                    <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase"
                                        letterSpacing="wider" color={labelColor} mb={2}>
                                        Núcleo de estoque *
                                    </Text>
                                    <Select value={distributorId} borderRadius="lg"
                                        onChange={e => {
                                            setDistributorId(e.target.value)
                                            setErrors(v => ({ ...v, distributor: null }))
                                            clearLot()
                                        }}
                                        isInvalid={!!errors.distributor}>
                                        <option value="">Selecione um núcleo</option>
                                        {warehouses.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                                    </Select>
                                    {errors.distributor && <Text fontSize="xs" color="red.500" mt={1}>{errors.distributor}</Text>}
                                </Box>

                                {/* Produto — autocomplete */}
                                <Box position="relative" ref={searchRef}>
                                    <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase"
                                        letterSpacing="wider" color={labelColor} mb={2}>
                                        Produto *
                                    </Text>
                                    <Input
                                        placeholder="Nome ou código do produto..."
                                        value={searchTerm}
                                        onChange={e => {
                                            setSearchTerm(e.target.value)
                                            setProductId(''); setProductName('')
                                            setShowDropdown(true)
                                            setErrors(v => ({ ...v, product: null }))
                                            clearLot()
                                        }}
                                        onFocus={() => searchTerm.length >= 2 && setShowDropdown(true)}
                                        onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
                                        isInvalid={!!errors.product}
                                        borderRadius="lg"
                                        autoComplete="off"
                                    />
                                    {errors.product && <Text fontSize="xs" color="red.500" mt={1}>{errors.product}</Text>}
                                    {productId && !errors.product && (
                                        <Flex align="center" gap={1.5} mt={1.5}>
                                            <Box w={1.5} h={1.5} borderRadius="full" bg="green.400" />
                                            <Text fontSize="xs" color="green.500" fontWeight="medium">Produto selecionado</Text>
                                        </Flex>
                                    )}

                                    {showDropdown && searchTerm.length >= 2 && (
                                        <Box position="absolute" zIndex={20} w="100%" bg={dropdownBg}
                                            border="1px solid" borderColor={cardBorder}
                                            borderRadius="xl" mt={1} maxH="230px" overflowY="auto"
                                            boxShadow="xl">
                                            {productSearch.isFetching ? (
                                                <Flex justify="center" align="center" gap={2} p={4}>
                                                    <Spinner size="sm" /><Text fontSize="sm" color={labelColor}>Buscando...</Text>
                                                </Flex>
                                            ) : productSearch.data?.length === 0 ? (
                                                <Flex align="center" gap={2} p={4} color={labelColor}>
                                                    <Icon name="search" boxSize={4} />
                                                    <Text fontSize="sm">Nenhum resultado para "{searchTerm}"</Text>
                                                </Flex>
                                            ) : productSearch.data?.map(p => (
                                                <Box key={p.id} px={4} py={3} cursor="pointer"
                                                    _hover={{ bg: dropdownHover }}
                                                    onMouseDown={() => handleSelectProduct(p)}
                                                    borderBottom="1px solid" borderColor={cardBorder}
                                                    _last={{ borderBottom: 'none' }}>
                                                    <Text fontSize="sm" fontWeight="semibold">{p.name}</Text>
                                                    {p.cod && <Text fontSize="xs" color={labelColor}>Cód: {p.cod}</Text>}
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            </Grid>

                            {/* ── Seleção de lote (opcional) ───────────────────── */}
                            {productId && distributorId && (
                                <Box
                                    border="1.5px solid"
                                    borderColor={selectedLotId ? 'teal.400' : cardBorder}
                                    borderRadius="xl"
                                    overflow="hidden"
                                    transition="border-color 0.2s"
                                    _dark={{ borderColor: selectedLotId ? 'teal.500' : undefined }}
                                >
                                    {/* Header do painel de lotes */}
                                    <Flex
                                        align="center" justify="space-between"
                                        px={5} py={3}
                                        bg={selectedLotId ? lotPanelSelBg : lotPanelBg}
                                        borderBottom="1px solid"
                                        borderColor={selectedLotId ? 'teal.200' : cardBorder}
                                        _dark={{ borderColor: selectedLotId ? 'teal.700' : undefined }}
                                    >
                                        <Flex align="center" gap={2}>
                                            <Icon name="stock" boxSize={4}
                                                color={selectedLotId ? 'teal.500' : labelColor} />
                                            <Text fontSize="sm" fontWeight="semibold">Lote associado</Text>
                                            <Badge colorScheme="gray" fontSize="2xs" borderRadius="full" px={2}>
                                                opcional
                                            </Badge>
                                        </Flex>
                                        {selectedLotId && (
                                            <Flex align="center" gap={2}>
                                                <Tag colorScheme="teal" size="sm">
                                                    <Text fontFamily="mono" fontSize="xs" fontWeight="bold">
                                                        {selectedLotNumber}
                                                    </Text>
                                                </Tag>
                                                <Box
                                                    as="button"
                                                    onClick={clearLot}
                                                    p={1}
                                                    borderRadius="md"
                                                    color={labelColor}
                                                    _hover={{ color: 'red.400', bg: 'red.50' }}
                                                    _dark={{ _hover: { bg: 'red.900' } }}
                                                    transition="all 0.15s"
                                                >
                                                    <Icon name="cancel" boxSize={3.5} />
                                                </Box>
                                            </Flex>
                                        )}
                                    </Flex>

                                    {/* Corpo do painel */}
                                    <Box p={4}>
                                        {distributorLots.isFetching ? (
                                            <Flex justify="center" align="center" gap={2} py={4}>
                                                <Spinner size="sm" />
                                                <Text fontSize="sm" color={labelColor}>Carregando lotes...</Text>
                                            </Flex>
                                        ) : lots.length === 0 ? (
                                            <Flex align="center" gap={2} py={2} color={labelColor}>
                                                <Icon name="stock" boxSize={4} />
                                                <Text fontSize="sm">
                                                    Nenhum lote disponível para este produto neste núcleo.
                                                    O ajuste incidirá sobre o saldo total.
                                                </Text>
                                            </Flex>
                                        ) : (
                                            <Flex gap={2} flexWrap="wrap">
                                                {lots.map(lot => {
                                                    const isSelected = selectedLotId === lot.id
                                                    const daysToExp  = lot.expires_at
                                                        ? Math.ceil((new Date(lot.expires_at + 'T00:00:00') - new Date()) / 86400000)
                                                        : null
                                                    const expColor   = daysToExp === null ? labelColor
                                                        : daysToExp < 0 ? 'red.500'
                                                        : daysToExp <= 30 ? 'orange.500'
                                                        : daysToExp <= 90 ? 'yellow.600'
                                                        : 'green.500'
                                                    return (
                                                        <Box
                                                            key={lot.id}
                                                            as="button"
                                                            onClick={() => handleSelectLot(lot)}
                                                            border="1.5px solid"
                                                            borderColor={isSelected ? 'teal.400' : lotCardBorder}
                                                            borderRadius="xl"
                                                            px={4} py={3}
                                                            bg={isSelected ? lotPanelSelBg : 'transparent'}
                                                            _hover={{ borderColor: 'teal.300', bg: lotCardHover }}
                                                            _dark={{
                                                                _hover: { bg: 'gray.700' },
                                                                bg: isSelected ? 'teal.900' : 'transparent',
                                                            }}
                                                            transition="all 0.15s"
                                                            textAlign="left"
                                                            minW="160px"
                                                            position="relative"
                                                        >
                                                            {isSelected && (
                                                                <Box
                                                                    position="absolute" top={2} right={2}
                                                                    w={2} h={2} borderRadius="full" bg="teal.400"
                                                                />
                                                            )}
                                                            <Text
                                                                fontSize="sm" fontFamily="mono" fontWeight="bold"
                                                                color={isSelected ? 'teal.600' : 'inherit'}
                                                                _dark={{ color: isSelected ? 'teal.300' : 'inherit' }}
                                                            >
                                                                {lot.lot_number}
                                                            </Text>
                                                            <Text fontSize="xs" color={labelColor} mt={1}>
                                                                Disp.: <Box as="span" fontWeight="semibold"
                                                                    color={lot.quantity <= 5 ? 'orange.500' : 'inherit'}>
                                                                    {lot.quantity} un.
                                                                </Box>
                                                            </Text>
                                                            {lot.expires_at && (
                                                                <Text fontSize="xs" color={expColor} mt={0.5}>
                                                                    Val.: {new Date(lot.expires_at + 'T00:00:00').toLocaleDateString('pt-BR')}
                                                                    {daysToExp !== null && daysToExp <= 90 && (
                                                                        <Box as="span" ml={1} fontWeight="semibold">
                                                                            ({daysToExp < 0 ? 'vencido' : `${daysToExp}d`})
                                                                        </Box>
                                                                    )}
                                                                </Text>
                                                            )}
                                                        </Box>
                                                    )
                                                })}
                                            </Flex>
                                        )}
                                    </Box>
                                </Box>
                            )}

                            <Divider />

                            {/* Operação + Quantidade lado a lado */}
                            <Grid templateColumns={{ base: '1fr', md: '2fr 1fr' }} gap={5} alignItems="end">
                                <Box>
                                    <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase"
                                        letterSpacing="wider" color={labelColor} mb={2}>
                                        Tipo de operação *
                                    </Text>
                                    <ButtonGroup w="100%" isAttached>
                                        <Button flex={1} size="md" borderLeftRadius="lg" borderRightRadius={0}
                                            colorScheme={isAdd ? 'green' : 'gray'}
                                            variant={isAdd ? 'solid' : 'outline'}
                                            leftIcon={<Icon name="addcircle" boxSize={4} />}
                                            onClick={() => setOperation('add')}>
                                            Adicionar ao estoque
                                        </Button>
                                        <Button flex={1} size="md" borderRightRadius="lg" borderLeftRadius={0}
                                            colorScheme={!isAdd ? 'red' : 'gray'}
                                            variant={!isAdd ? 'solid' : 'outline'}
                                            leftIcon={<Icon name="losses" boxSize={4} />}
                                            onClick={() => setOperation('subtract')}>
                                            Subtrair do estoque
                                        </Button>
                                    </ButtonGroup>
                                </Box>

                                <Box>
                                    <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase"
                                        letterSpacing="wider" color={labelColor} mb={2}>
                                        Quantidade *
                                    </Text>
                                    <NumberInput min={1} value={qty}
                                        onChange={v => { setQty(Number(v)); setErrors(e => ({ ...e, qty: null })) }}
                                        isInvalid={!!errors.qty}>
                                        <NumberInputField borderRadius="lg" fontWeight="semibold" fontSize="lg" />
                                        <NumberInputStepper>
                                            <NumberIncrementStepper />
                                            <NumberDecrementStepper />
                                        </NumberInputStepper>
                                    </NumberInput>
                                    {errors.qty && <Text fontSize="xs" color="red.500" mt={1}>{errors.qty}</Text>}
                                </Box>
                            </Grid>

                            {/* Motivo */}
                            <Box>
                                <Flex justify="space-between" mb={2}>
                                    <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase"
                                        letterSpacing="wider" color={labelColor}>
                                        Motivo do ajuste *
                                    </Text>
                                    <Text fontSize="xs" color={reason.length < 10 ? 'red.400' : labelColor}>
                                        {reason.length}/500
                                    </Text>
                                </Flex>
                                <Textarea rows={4} borderRadius="lg"
                                    placeholder="Descreva o motivo (ex: Divergência encontrada no inventário físico, produto contabilizado em duplicata...)"
                                    value={reason}
                                    onChange={e => { setReason(e.target.value); setErrors(v => ({ ...v, reason: null })) }}
                                    isInvalid={!!errors.reason}
                                />
                                {errors.reason && <Text fontSize="xs" color="red.500" mt={1}>{errors.reason}</Text>}
                            </Box>

                            {/* Botão */}
                            <Button size="lg" borderRadius="xl" colorScheme={opColor}
                                leftIcon={<Icon name={isAdd ? 'addcircle' : 'losses'} />}
                                onClick={() => { if (validate()) auth.onOpen() }}
                                isLoading={loading}>
                                {isAdd ? 'Confirmar adição' : 'Confirmar subtração'}
                                {productName ? ` — ${productName}` : ''}
                            </Button>
                        </Box>
                    </Box>
                </GridItem>

                {/* ── Painel lateral ─────────────────────────────────── */}
                <GridItem>
                    <Box display="flex" flexDirection="column" gap={5}>

                        {/* Saldo em tempo real */}
                        <Box bg={cardBg} border="1px solid" borderColor={cardBorder} borderRadius="2xl" overflow="hidden">
                            <Flex align="center" gap={2} px={6} py={4} borderBottom="1px solid" borderColor={cardBorder}>
                                <Icon name="stock" boxSize={4} color={labelColor} />
                                <Text fontWeight="semibold" fontSize="sm">Saldo atual</Text>
                            </Flex>
                            <Box p={6}>
                                {!distributorId || !productId ? (
                                    <Flex direction="column" align="center" gap={3} py={6}>
                                        <Icon name="stock" boxSize={12} color="gray.300" />
                                        <Text fontSize="xs" color={labelColor} textAlign="center">
                                            Selecione um núcleo e produto para ver o saldo
                                        </Text>
                                    </Flex>
                                ) : currentStock.isFetching ? (
                                    <Flex justify="center" py={6}><Spinner /></Flex>
                                ) : !stock ? (
                                    <Flex direction="column" align="center" gap={2} py={4}>
                                        <Tag colorScheme="orange" size="sm">Sem estoque</Tag>
                                        <Text fontSize="xs" color={labelColor} textAlign="center">
                                            Produto sem registro neste núcleo
                                        </Text>
                                    </Flex>
                                ) : (
                                    <Box display="flex" flexDirection="column" gap={2}>
                                        {[
                                            { label: 'Disponível',   value: stock.available, scheme: stock.available <= 5 ? 'orange' : 'green' },
                                            { label: 'Reservado',    value: stock.reserved,  scheme: 'yellow'  },
                                            { label: 'Bloqueado',    value: stock.blocked,   scheme: 'purple'  },
                                            { label: 'Total físico', value: stock.stock,     scheme: 'blue'    },
                                        ].map(({ label, value, scheme }) => (
                                            <Flex key={label} justify="space-between" align="center"
                                                px={4} py={3} borderRadius="lg"
                                                bg={stockRowBg} border="1px solid" borderColor={stockRowBorder}>
                                                <Text fontSize="sm" color={labelColor}>{label}</Text>
                                                <Tag colorScheme={scheme} size="sm" fontWeight="bold">{value ?? 0}</Tag>
                                            </Flex>
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        </Box>

                        {/* Preview do resultado */}
                        {stock && productId && (
                            <Box bg={opPanelBg} border="1px solid" borderColor={opPanelBord}
                                borderRadius="2xl" overflow="hidden">
                                <Flex align="center" gap={2} px={6} py={4}
                                    borderBottom="1px solid" borderColor={opPanelBord}>
                                    <Icon name={isAdd ? 'addcircle' : 'losses'} boxSize={4} color={`${opColor}.400`} />
                                    <Text fontWeight="semibold" fontSize="sm">Resultado após ajuste</Text>
                                </Flex>
                                <Box p={6} display="flex" flexDirection="column" gap={3}>
                                    {[
                                        { label: 'Estoque atual',                    value: stock.stock },
                                        { label: isAdd ? '+ Adição' : '− Subtração', value: isAdd ? `+${qty}` : `-${qty}`, accent: true },
                                        { label: 'Novo total físico',                value: isAdd ? stock.stock + qty : stock.stock - qty, bold: true },
                                    ].map(({ label, value, accent, bold }) => (
                                        <Flex key={label} justify="space-between" align="center"
                                            borderBottom={!bold ? '1px dashed' : 'none'}
                                            borderColor={opPanelBord} pb={bold ? 0 : 3}>
                                            <Text fontSize="sm" color={labelColor}>{label}</Text>
                                            <Text
                                                fontSize={bold ? 'lg' : 'sm'}
                                                fontWeight={bold ? 'bold' : accent ? 'semibold' : 'normal'}
                                                color={accent ? `${opColor}.500` : 'inherit'}>
                                                {value}
                                            </Text>
                                        </Flex>
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {/* Lote selecionado — resumo */}
                        {selectedLotId && (
                            <Box bg={cardBg} border="1.5px solid" borderColor="teal.300"
                                borderRadius="2xl" overflow="hidden"
                                _dark={{ borderColor: 'teal.600' }}>
                                <Flex align="center" gap={2} px={6} py={4}
                                    borderBottom="1px solid" borderColor="teal.100"
                                    _dark={{ borderColor: 'teal.800' }}>
                                    <Icon name="stock" boxSize={4} color="teal.400" />
                                    <Text fontWeight="semibold" fontSize="sm" color="teal.600"
                                        _dark={{ color: 'teal.300' }}>
                                        Lote selecionado
                                    </Text>
                                </Flex>
                                <Box p={6} display="flex" flexDirection="column" gap={2}>
                                    {[
                                        { label: 'Número',     value: selectedLotNumber },
                                        { label: 'Disponível', value: `${selectedLotQty} un.` },
                                    ].map(({ label, value }) => (
                                        <Flex key={label} justify="space-between" align="center"
                                            px={4} py={3} borderRadius="lg"
                                            bg={stockRowBg} border="1px solid" borderColor={stockRowBorder}>
                                            <Text fontSize="sm" color={labelColor}>{label}</Text>
                                            <Text fontSize="sm" fontWeight="semibold" fontFamily={label === 'Número' ? 'mono' : undefined}>
                                                {value}
                                            </Text>
                                        </Flex>
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {/* Aviso */}
                        <Box bg={formSectionBg} border="1px solid" borderColor={cardBorder}
                            borderRadius="2xl" p={5}>
                            <Flex align="flex-start" gap={3}>
                                <Icon name="fiscal" boxSize={4} color="blue.400" mt={0.5} flexShrink={0} />
                                <Text fontSize="xs" color={labelColor} lineHeight="1.6">
                                    O ajuste é registrado no ledger de movimentações com seu usuário, IP e motivo.
                                    Não é possível reverter — em caso de erro, realize um novo ajuste na direção oposta.
                                </Text>
                            </Flex>
                        </Box>
                    </Box>
                </GridItem>
            </Grid>

            <Authenticate {...auth} onSuccess={handleSubmit} />
        </Container>
    )
}
